import {
  Paper,
  Box,
  Typography,
  Divider,
  Checkbox,
  MenuItem,
  FormControl,
  ListItemText,
  OutlinedInput,
} from '@mui/material'
import Select from '@mui/material/Select'
import { DropDown } from 'Common/Supplyvue-ui'
import {
  FILE_NAME,
  FILE_VERSION,
  SET_FILE_NAME,
  SET_FILE_VERSION,
  SET_KEY_FIGURES,
  KEY_FIGURES_ARRAY,
  SET_WORKCENTRES,
} from './DownloadData.constants'
import { PLANNING_BOOK_LEDGER } from 'Features/DownloadDataModal/DownloadData.constants'
import {
  DataDownloadSettingsProps,
  WorkcentreResponse,
} from 'Features/OperationalPlanner/DownloadDataModal/DownloadDataModal.types'
import { useState, useEffect } from 'react'
import { useWorkcentreStatus } from 'Features/OperationalPlanner/OperationalPlan.data'
import { Loader } from 'Common/Loader/Loader'
import { WORKCENTRE_STATUS } from 'Common/Constants/Constants'
import { skuCodeSort } from 'Common/utils/utils'

export const DownloadDataView = ({
  planId,
  fileNameLabel,
  fileVersionLabel,
  keyFiguresLabel,
  fileName,
  fileVersion,
  keyFigures,
  updateState,
  workcentres,
  fileNames = FILE_NAME,
  keyFigureItems = KEY_FIGURES_ARRAY,
  keyFigureLabels,
  onWorkcentreKeys,
}: DataDownloadSettingsProps) => {
  const { data: workCentreSummary, isLoading } = useWorkcentreStatus(planId)
  const keyFiguresItems = keyFigureItems
  // The ledger's key figures are codes; the menu shows their labels.
  const keyFigureLabel = (value: string) => keyFigureLabels?.[value] ?? value
  const [workcentreItems, SetWorkCentreItems] = useState<string[]>([])
  const ITEM_HEIGHT = 38
  const ITEM_PADDING_TOP = 8
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 15 + ITEM_PADDING_TOP,
      },
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'right',
    },
    getContentAnchorEl: null,
    autoFocus: false,
  }

  const FileVersionDropDown = () => {
    const [selectedVersions, setSelectedVersions] = useState<string[]>([
      ...fileVersion,
    ])

    const onClose = () => {
      updateState({
        type: SET_FILE_VERSION,
        payload: selectedVersions,
      })
    }

    const handleClick = (value) => {
      const newVersions = [...selectedVersions]

      const index = newVersions.indexOf(value)
      if (index > -1) {
        newVersions.splice(index, 1)
      } else {
        newVersions.push(value)
      }
      setSelectedVersions(newVersions)
    }

    return (
      <Box>
        <FormControl fullWidth={true}>
          <Select
            labelId="File Versions Dropdown"
            id="File Versions Dropdown"
            multiple
            value={selectedVersions}
            fullWidth={true}
            onClose={onClose}
            input={<OutlinedInput />}
            renderValue={(selected) => selected.join(', ')}
            MenuProps={MenuProps}
          >
            {FILE_VERSION.map((name) => (
              <MenuItem
                key={name}
                value={name}
                onClick={() => handleClick(name)}
              >
                <Checkbox
                  checked={selectedVersions.indexOf(name) > -1}
                  value={name}
                />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    )
  }

  const MultipleSelectWorkcentres = () => {
    const [selectAll, setSelectAll] = useState<boolean>(true)
    const [selectedWorkcentres, setSelectedWorkcentres] =
      useState<string[]>(workcentres)

    const handleSelectAll = (value) => {
      if (selectAll) {
        setSelectedWorkcentres([])
      } else {
        setSelectedWorkcentres(workcentreItems)
      }
      setSelectAll(!value)
    }

    const onClose = () => {
      updateState({
        type: SET_WORKCENTRES,
        payload: selectedWorkcentres,
      })
    }

    const handleClick = (value) => {
      const newItems = [...selectedWorkcentres]

      const index = newItems.indexOf(value)
      if (index > -1) {
        newItems.splice(index, 1)
      } else {
        newItems.push(value)
      }
      setSelectedWorkcentres(newItems)
    }

    return (
      <Box>
        {isLoading ? (
          <Loader />
        ) : (
          <FormControl fullWidth={true}>
            <Select
              labelId="multiple-checkbox-label"
              id="multiple-checkbox"
              multiple
              value={selectedWorkcentres}
              fullWidth={true}
              onClose={onClose}
              input={<OutlinedInput />}
              renderValue={(selected) => selected.join(', ')}
              MenuProps={MenuProps}
            >
              <MenuItem
                value={selectAll}
                onClick={() => handleSelectAll(selectAll)}
              >
                <Checkbox checked={selectAll} />
                <ListItemText primary="Select/Deselect All" />
              </MenuItem>
              <Divider
                sx={{
                  height: '3px',
                  background: '#006286',
                  width: '100%',
                }}
              />
              {workcentreItems?.map((name) => (
                <MenuItem
                  key={name}
                  value={name}
                  onClick={() => handleClick(name)}
                >
                  <Checkbox
                    checked={selectedWorkcentres.indexOf(name) > -1}
                    value={name}
                  />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    )
  }

  const MultipleSelectKeyFigures = () => {
    const [selectedKeyFigures, setSelectedKeyFigures] = useState<string[]>([
      ...keyFigures,
    ])
    const [selectAll, setSelectAll] = useState<boolean>(true)

    useEffect(() => {
      if (keyFigures.length > 0) {
        setSelectedKeyFigures(keyFigures)
        setSelectAll(true)
      }
    }, [keyFigures])
    const handleSelectAll = (value) => {
      if (selectAll) {
        setSelectedKeyFigures([])
      } else {
        setSelectedKeyFigures(keyFiguresItems)
      }
      setSelectAll(!value)
    }

    const onClose = () => {
      updateState({
        type: SET_KEY_FIGURES,
        payload: selectedKeyFigures,
      })
    }

    const handleClick = (value) => {
      const newItems = [...selectedKeyFigures]

      const index = newItems.indexOf(value)
      if (index > -1) {
        newItems.splice(index, 1)
      } else {
        newItems.push(value)
      }
      setSelectedKeyFigures(newItems)
    }
    return (
      <Box>
        <FormControl
          fullWidth={true}
          disabled={
            fileName !== 'DRP Outputs' &&
            fileName !== 'DRP Outputs (Pivot)' &&
            fileName !== PLANNING_BOOK_LEDGER
          }
        >
          <Select
            labelId="demo-multiple-checkbox-label"
            id="demo-multiple-checkbox"
            multiple
            value={selectedKeyFigures}
            fullWidth={true}
            onClose={onClose}
            input={<OutlinedInput />}
            renderValue={(selected) => selected.map(keyFigureLabel).join(', ')}
            MenuProps={MenuProps}
          >
            <MenuItem
              value={selectAll}
              onClick={() => handleSelectAll(selectAll)}
            >
              <Checkbox checked={selectAll} />
              <ListItemText primary="Select/Deselect All" />
            </MenuItem>
            <Divider
              sx={{
                height: '3px',
                background: '#006286',
                width: '100%',
              }}
            />
            {keyFiguresItems?.map((name) => (
              <MenuItem
                key={name}
                value={name}
                onClick={() => handleClick(name)}
              >
                <Checkbox
                  checked={selectedKeyFigures.indexOf(name) > -1}
                  value={name}
                />
                <ListItemText primary={keyFigureLabel(name)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    )
  }

  useEffect(() => {
    if (workCentreSummary && workcentreItems !== []) {
      // Each code keeps its work-centre key: the ledger option is keyed by key.
      const keysByCode: Record<string, string[]> = {}
      const NewWorkcentreItems = workCentreSummary
        ?.map((item: WorkcentreResponse) => {
          if (item.status === WORKCENTRE_STATUS.SUCCESS && item.workCentre) {
            keysByCode[item.workCentre] = [
              ...(keysByCode[item.workCentre] ?? []),
              String(item.workCentreKey),
            ]
            return item.workCentre
          }
        })
        .filter((item) => item)
        .sort(skuCodeSort)
      onWorkcentreKeys?.(keysByCode)
      SetWorkCentreItems(NewWorkcentreItems)
      updateState({
        type: SET_WORKCENTRES,
        payload: NewWorkcentreItems,
      })
    }
  }, [workCentreSummary])

  return (
    <>
      <Paper elevation={0}>
        <Box display="flex" minHeight="20vh" flexDirection="column">
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            mt={2}
          >
            <Box display="flex" flexDirection="column">
              <Box width="15rem">
                <Typography variant="h4">Metric</Typography>
              </Box>
            </Box>

            <Box display="flex" flexDirection="column">
              <Box width="15rem">
                <Typography variant="h4">Action</Typography>
              </Box>
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Divider
              flexItem
              sx={{
                height: '3px',
                background: '#006286',
                width: '100%',
                marginTop: '1rem',
              }}
            />
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
          >
            <Box display="flex" flexDirection="column">
              <Box width="15rem">
                <Typography variant="h4">{fileNameLabel}</Typography>
              </Box>
            </Box>
            <Box display="flex" flexDirection="column">
              <Box width="15rem">
                <DropDown
                  id={fileNameLabel}
                  value={fileName}
                  sx={{
                    minWidth: '150px',
                  }}
                  fullWidth={true}
                  onChange={(e) =>
                    updateState({
                      type: SET_FILE_NAME,
                      payload: e.target.value as string,
                    })
                  }
                  menuItems={fileNames}
                  label={fileNameLabel}
                />
              </Box>
            </Box>
          </Box>

          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
          >
            <Box display="flex" flexDirection="column">
              <Box width="15rem">
                <Typography variant="h4">{fileVersionLabel}</Typography>
              </Box>
            </Box>
            <Box display="flex" flexDirection="column" width="15rem">
              <FileVersionDropDown />
            </Box>
          </Box>
          <Box
            mt={2}
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" flexDirection="column">
              <Box width="15rem">
                <Typography variant="h4">{keyFiguresLabel}</Typography>
              </Box>
            </Box>
            <Box display="flex" flexDirection="column" width="15rem">
              <MultipleSelectKeyFigures />
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
          >
            <Box display="flex" flexDirection="column">
              <Box width="15rem">
                <Typography variant="h4">Workcentres</Typography>
              </Box>
            </Box>
            <Box display="flex" flexDirection="column" width="15rem">
              <MultipleSelectWorkcentres />
            </Box>
          </Box>
        </Box>
      </Paper>
    </>
  )
}
